import React, { useEffect, useState, useRef } from 'react';
import { FaStar, FaSpinner } from 'react-icons/fa';
import { DurationSelector } from './DurationSelector';
import HintTooltip from './HintTooltip';
import './UnifiedSpotlightModal.scss';

interface UnifiedSpotlightModalProps {
  isOpen: boolean;
  title: string; // "Spotlight Post" or "Spotlight Profile" or "Spotlight DM"
  onConfirm: (durationHours: number) => void;
  onCancel: () => void;
  onDurationChange: (durationHours: number) => Promise<{
    price: number;
    canProceed?: boolean;
    warning?: string;
    balance?: {
      current: number;
      required: string;
      shortfall: string | null;
    };
  }>; // Returns total price and optional warning info
  isProcessing: boolean;
  defaultDuration?: number;
  showDurationSelector?: boolean; // Hide duration selector for DM spotlight
  description?: string; // Custom description
  /** Error from the most recent submit attempt. Displayed inline so the
   *  user doesn't have to chase a fading toast. */
  submissionError?: string | null;
  /** Called when the user takes an action that should clear a stale
   *  submission error (retry confirm, change duration). Caller is the
   *  source of truth so the error doesn't reappear on the next render. */
  onClearError?: () => void;
  /** What this spotlight type does + who sees it. Used by the header
   *  HintTooltip. Caller provides because the reach differs per kind
   *  (profile/post = location-scoped, DM = recipient only). */
  explainer?: string;
}

/**
 * Unified Spotlight Modal - Combines duration selection and confirmation
 * Shows only the total price to keep things simple for users
 */
export const UnifiedSpotlightModal: React.FC<UnifiedSpotlightModalProps> = ({
  isOpen,
  title,
  onConfirm,
  onCancel,
  onDurationChange,
  isProcessing,
  defaultDuration = 24,
  showDurationSelector = true,
  description,
  submissionError,
  onClearError,
  explainer,
}) => {
  const [selectedDuration, setSelectedDuration] = useState(defaultDuration);
  const [totalPrice, setTotalPrice] = useState<number | null>(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [canProceed, setCanProceed] = useState(true);
  const [warning, setWarning] = useState<string | undefined>(undefined);
  const [balance, setBalance] = useState<{ current: number; required: string; shortfall: string | null } | undefined>(undefined);
  const hasLoadedInitialPrice = useRef(false);

  // Load initial price when modal first opens (with small delay to prevent rerender flash)
  useEffect(() => {
    if (!isOpen) {
      // Reset when modal closes
      hasLoadedInitialPrice.current = false;
      setTotalPrice(null);
      setSelectedDuration(defaultDuration);
      setCanProceed(true);
      setWarning(undefined);
      setBalance(undefined);
      return;
    }

    // Load default price after modal is rendered
    if (!hasLoadedInitialPrice.current) {
      const timer = setTimeout(() => {
        hasLoadedInitialPrice.current = true;
        loadPrice(defaultDuration);
      }, 100); // Small delay to let modal render first

      return () => clearTimeout(timer);
    }
  }, [isOpen, defaultDuration]);

  const loadPrice = async (duration: number) => {
    setIsLoadingPrice(true);
    try {
      const result = await onDurationChange(duration);
      setTotalPrice(result.price);
      setCanProceed(result.canProceed !== false);
      setWarning(result.warning);
      setBalance(result.balance);
    } catch (err: any) {
      console.error('Failed to load price:', err);
      setTotalPrice(null);
      setCanProceed(false);
      setWarning('Failed to load pricing information');
    } finally {
      setIsLoadingPrice(false);
    }
  };

  const handleDurationSelect = async (duration: number) => {
    setSelectedDuration(duration);
    hasLoadedInitialPrice.current = true;
    // Stale submission error doesn't apply to a different duration.
    onClearError?.();

    // Load price immediately when user selects duration
    setIsLoadingPrice(true);
    try {
      const result = await onDurationChange(duration);
      setTotalPrice(result.price);
      setCanProceed(result.canProceed !== false);
      setWarning(result.warning);
      setBalance(result.balance);
    } catch (err: any) {
      console.error('Failed to load price:', err);
      setTotalPrice(null);
      setCanProceed(false);
      setWarning('Failed to load pricing information');
    } finally {
      setIsLoadingPrice(false);
    }
  };

  const handleConfirm = () => {
    // Retry: clear any error from the prior attempt before kicking off the
    // new submit so the user sees the in-flight UI cleanly.
    onClearError?.();
    onConfirm(selectedDuration);
  };

  if (!isOpen) return null;

  return (
    <div className="unified-spotlight-overlay" onClick={onCancel}>
      <div className="unified-spotlight-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>
            <FaStar className="modal-icon" /> {title}
            {explainer && (
              <>
                {' '}
                <HintTooltip text={explainer} placement="bottom" />
              </>
            )}
          </h2>
          <button className="btn-close" onClick={onCancel} disabled={isProcessing}>
            ×
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          <p className="modal-description">
            {description || (showDurationSelector ? 'Choose how long you want this to be spotlighted.' : '')}
          </p>

          {/* Warning Banner for Insufficient Funds */}
          {warning && (
            <div className="warning-banner">
              <span className="warning-icon">⚠️</span>
              <div className="warning-content">
                <strong>Insufficient Funds</strong>
                <p>{warning}</p>
                {balance && (
                  <div className="balance-info">
                    <div>Current Balance: <strong>{balance.current.toFixed(2)} KLV</strong></div>
                    <div>Required: <strong>{balance.required} KLV</strong></div>
                    <div className="shortfall">Shortfall: <strong>{balance.shortfall} KLV</strong></div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Duration Selector */}
          {showDurationSelector && (
            <DurationSelector
              selectedDuration={selectedDuration}
              onSelect={handleDurationSelect}
            />
          )}

          {/* Total Price Display */}
          <div className="price-display">
            <div className="price-label">Total Cost</div>
            <div className="price-value">
              {isLoadingPrice ? (
                <span className="loading-price">
                  <FaSpinner className="spinner" /> Calculating...
                </span>
              ) : totalPrice !== null ? (
                <span className="price-amount">{totalPrice.toFixed(2)} KLV</span>
              ) : (
                <span className="price-error">Price unavailable</span>
              )}
            </div>
          </div>

          {/* Submission Error Banner */}
          {submissionError && (() => {
            // Detect the common "insufficient balance" path so we surface a
            // concrete heading instead of a generic 'Spotlight failed'. The
            // raw message comes from the backend or the wallet SDK — both
            // tend to include the words 'insufficient' or 'balance' when
            // the wallet runs out of funds.
            const lowered = submissionError.toLowerCase();
            const isInsufficient =
              lowered.includes('insufficient') ||
              lowered.includes('not enough') ||
              (lowered.includes('balance') && lowered.includes('need'));
            return (
              <div
                className={`submission-error-banner${isInsufficient ? ' submission-error-banner--funds' : ''}`}
                role="alert"
              >
                <span className="submission-error-icon">⚠️</span>
                <div className="submission-error-content">
                  <strong>
                    {isInsufficient ? 'Insufficient funds' : 'Spotlight failed'}
                  </strong>
                  <p>{submissionError}</p>
                </div>
              </div>
            );
          })()}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleConfirm}
            disabled={!selectedDuration || isProcessing || isLoadingPrice || totalPrice === null || !canProceed}
          >
            {isProcessing ? (
              <>
                <FaSpinner className="spinner" /> Processing...
              </>
            ) : (
              <>
                <FaStar /> Confirm Spotlight
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnifiedSpotlightModal;
